"use client"

import type { FC } from "react"
import { useEffect, useRef, useState } from "react"
import { IconButton } from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import "./SearchResults.css"

interface SearchResultItem {
  project_id: number
  project_name: string
  stage_number?: number
  stage_name?: string
  section_number?: number
  section_name?: string
  subsection_number?: number
  subsection_name?: string
  status: number
}

interface SearchResultsProps {
  results: SearchResultItem[]
  searchTerm: string
  onClose: () => void
}


function highlightText(text: string, query: string): React.ReactNode {
  if (!query) return text
  const regex = new RegExp(`(${query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, "gi")
  const parts = text.split(regex)
  return parts.map((part, i) =>
    regex.test(part) ? <strong key={i} className="highlight">{part}</strong> : part
  )
}

const SearchResults: FC<SearchResultsProps> = ({ results, searchTerm, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  // WCAG 2.1.1 (Level A): Track focused result index for keyboard navigation
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const resultRefs = useRef<(HTMLDivElement | null)[]>([])
  // Track if user has entered the results area (via Enter key)
  const [isInResultsArea, setIsInResultsArea] = useState<boolean>(false)

  useEffect(() => {
    // Initialize refs array
    resultRefs.current = resultRefs.current.slice(0, results.length)
  }, [results.length])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    // WCAG 2.1.1 (Level A): Handle keyboard navigation with arrow keys and focus trap
    const handleKeyDown = (event: KeyboardEvent) => {
      // Close dialog on Escape
      if (event.key === "Escape" || event.key === "Esc") {
        onClose();
        return;
      }

      // Trap Tab key to keep focus within dialog
      if (event.key === "Tab") {
        event.preventDefault();

        // Get all focusable elements
        const focusableElements: HTMLElement[] = [
          closeButtonRef.current,
          ...resultRefs.current.filter(Boolean)
        ].filter((el): el is HTMLButtonElement | HTMLDivElement => el !== null) as HTMLElement[];

        if (focusableElements.length === 0) return;

        const currentIndex = focusableElements.findIndex(el => el === document.activeElement);

        if (event.shiftKey) {
          // Shift+Tab: move backward
          const prevIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
          focusableElements[prevIndex]?.focus();
        } else {
          // Tab: move forward
          const nextIndex = currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
          focusableElements[nextIndex]?.focus();
        }
        return;
      }

      // Enter key moves focus from input to results area
      if (event.key === "Enter" && !isInResultsArea) {
        event.preventDefault();
        setIsInResultsArea(true);
        // Focus the close button first when entering results area
        if (closeButtonRef.current) {
          closeButtonRef.current.focus();
        } else if (results.length > 0) {
          setFocusedIndex(0);
          resultRefs.current[0]?.focus();
        }
        return;
      }

      // Navigate results with arrow keys only when in results area
      if (isInResultsArea && results.length > 0) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setFocusedIndex(prev => {
            const nextIndex = prev < results.length - 1 ? prev + 1 : 0;
            // Focus the next result element
            resultRefs.current[nextIndex]?.focus();
            return nextIndex;
          });
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          setFocusedIndex(prev => {
            const nextIndex = prev > 0 ? prev - 1 : results.length - 1;
            // Focus the previous result element
            resultRefs.current[nextIndex]?.focus();
            return nextIndex;
          });
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    }
  }, [onClose, results.length, isInResultsArea])

  const renderHierarchy = (item: SearchResultItem, index: number) => {
    const parts = []

       if (item.project_name) {
      const projText = highlightText(item.project_name, searchTerm)
      parts.push(<span key="p" className="project">{projText}</span>)
    }
    if (item.stage_name) {
      const txt = `${item.stage_name}`
      parts.push(
        // WCAG 1.1.1 (Level A): Decorative images must have empty alt text
        <img key="a1" src="/images/arrow-left.png" className="arrow" alt="" role="presentation" />,
        <span key="s" className={item.stage_name.toLowerCase().includes(searchTerm.toLowerCase()) ? "bold-line" : ""}>
          {highlightText(txt, searchTerm)}
        </span>
      )
    }
    if (item.section_name) {
      const txt = `${item.section_name}`
      parts.push(
        // WCAG 1.1.1 (Level A): Decorative images must have empty alt text
        <img key="a2" src="/images/arrow-left.png" className="arrow" alt="" role="presentation" />,
        <span key="sec" className={item.section_name.toLowerCase().includes(searchTerm.toLowerCase()) ? "bold-line" : ""}>
          {highlightText(txt, searchTerm)}
        </span>
      )
    }
    if (item.subsection_name) {
      const txt = `${item.subsection_name}`
      parts.push(
        // WCAG 1.1.1 (Level A): Decorative images must have empty alt text
        <img key="a3" src="/images/arrow-left.png" className="arrow" alt="" role="presentation" />,
        <span key="sub" className={item.subsection_name.toLowerCase().includes(searchTerm.toLowerCase()) ? "bold-line" : ""}>
          {highlightText(txt, searchTerm)}
        </span>
      )
    }

    // Build accessible text description for screen readers
    const accessibleText = [
      item.project_name,
      item.stage_name,
      item.section_name,
      item.subsection_name
    ].filter(Boolean).join(', ')

    return (
      // WCAG 2.1.1 (Level A): Make interactive element keyboard accessible with arrow key navigation
      // WCAG 4.1.2 (Level A): Use role="link" for navigation to project pages
      <div
        ref={(el) => { resultRefs.current[index] = el }}
        className="hierarchy clickable"
        onClick={() => {
          onClose()
          navigate(`/project-about/${item.project_id}`)
        }}
        onKeyDown={(e) => {
          // WCAG 2.1.1 (Level A): Support Enter key activation for links
          if (e.key === 'Enter') {
            e.preventDefault()
            onClose()
            navigate(`/project-about/${item.project_id}`)
          }
        }}
        role="link"
        tabIndex={0}
        aria-label={`עבור לפרויקט: ${accessibleText}`}
      >
        {parts}
      </div>
    )
  }

  return (
    <div className="popup-overlay">
      {/* WCAG 4.1.2 (Level A): Dialog must have aria-labelledby and aria-describedby */}
      <div
        role="dialog"
        aria-labelledby="search-dialog-title"
        aria-describedby="search-dialog-description"
        aria-modal="true"
        className="search-results-container"
        ref={containerRef}
      >
        <div className="search-results-header">
            {/* WCAG 4.1.2 (Level A): Close button needs accessible label */}
            <IconButton
              ref={closeButtonRef}
              onClick={onClose}
              size="small"
              className="close-button"
              aria-label="סגור חלון תוצאות חיפוש"
            >
                <CloseIcon fontSize="small" aria-hidden="true" />
            </IconButton>
          <div>
            {/* WCAG 1.3.1 (Level A): Use proper heading for dialog title */}
            <h2 id="search-dialog-title">תוצאות חיפוש עבור: "{searchTerm}"</h2>
                {/* WCAG 4.1.3 (Level AA): Announce results count to screen readers */}
            <p id="search-dialog-description" aria-live="polite" aria-atomic="true">
              נמצאו {results.length} תוצאות
            </p>
          </div>
        </div>

        {results.length > 0 ? (
            // WCAG 1.3.1 (Level A): Use semantic list for search results
            <ul className="search-results-list" role="list">
                {results.map((item, index) => (
                    <li
                        className="search-result-row"
                        key={`${item.project_id}-${index}`}>
                        {renderHierarchy(item, index)}
                    </li>
                ))}
            </ul>
        ) : (
          // WCAG 4.1.3 (Level AA): Announce no results to screen readers
          <div className="no-results" role="status" aria-live="polite">
            לא נמצאו תוצאות עבור חיפוש זה
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchResults
